import React from 'react'
import styled from 'styled-components'
import Header from '../common/header'
import Footer from '../common/footer'
import Spacer from '../lib/spacer'
import StoreLinks from '../common/store_links'
import { useTranslation } from '../lib/i18n'
import { formatDate } from '../blog/format_date'
import { small } from '../lib/media_query'
import releases from './releases.json'

const PageHeader = styled.div`
    padding: 60px 0 0;

    h1 {
        font-size: 42px;
        font-weight: 700;
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
        margin: 0;
    }

    ${small(`
        padding: 40px 0 0;
        h1 { font-size: 32px; }
    `)}
`

const Timeline = styled.div`
    position: relative;
    max-width: 820px;
    margin: 48px 0 0;
    padding-left: 34px;

    &::before {
        content: '';
        position: absolute;
        left: 5px;
        top: 10px;
        bottom: 60px;
        width: 2px;
        background: linear-gradient(
            180deg,
            ${(props) => props.theme.palette.brand.blue} 0%,
            ${(props) => props.theme.palette.greyScale.lighterGrey} 45%,
            rgba(231, 231, 231, 0) 100%
        );
    }

    ${small(`
        margin-top: 32px;
        padding-left: 26px;
    `)}
`

const Release = styled.section`
    position: relative;
    padding-bottom: 36px;

    &:last-child {
        padding-bottom: 0;
    }

    &::before {
        content: '';
        position: absolute;
        left: -34px;
        top: 6px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #ffffff;
        border: 2px solid
            ${(props) =>
                props.$latest
                    ? props.theme.palette.brand.blue
                    : props.theme.palette.greyScale.lightGrey};
        box-shadow: ${(props) =>
            props.$latest ? '0 0 0 5px rgba(32, 159, 255, 0.16)' : 'none'};
    }

    ${small(`
        &::before {
            left: -26px;
        }
    `)}
`

const ReleaseHeading = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 14px;
`

const Version = styled.h2`
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.5px;
    color: ${(props) => props.theme.palette.greyScale.darkGrey};
    margin: 0;
    font-variant-numeric: tabular-nums;

    ${small(`
        font-size: 22px;
    `)}
`

const ReleaseDate = styled.span`
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: ${(props) => props.theme.palette.greyScale.lightGrey};
`

const Notes = styled.div`
    border-radius: 14px;
    padding: 22px 26px;
    background: ${(props) =>
        props.$latest ? '#ffffff' : props.theme.palette.greyScale.lightestGrey};
    border: 1px solid
        ${(props) =>
            props.$latest
                ? 'rgba(32, 159, 255, 0.35)'
                : props.theme.palette.greyScale.lighterGrey};
    box-shadow: ${(props) =>
        props.$latest ? '0 10px 30px rgba(32, 159, 255, 0.12)' : 'none'};
    font-size: 15.5px;
    line-height: 1.7;
    color: ${(props) => props.theme.palette.greyScale.grey};

    ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    li {
        position: relative;
        padding-left: 22px;

        &:not(:last-child) {
            margin-bottom: 10px;
        }

        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 10px;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: ${(props) => props.theme.palette.greyScale.lightGrey};
        }
    }

    p {
        margin-bottom: 14px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    strong {
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
        font-weight: 600;
    }

    a {
        color: ${(props) => props.theme.palette.brand.blue};
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    ${small(`
        padding: 18px 20px;
        font-size: 15px;
    `)}
`

const DownloadRow = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 64px;

    ${small(`
        margin-top: 48px;
    `)}
`

export default function Changelog() {
    const { t } = useTranslation()
    const dateLocale = t('dateLocale')

    return (
        <div>
            <Header />
            <div className="grid-container">
                <PageHeader>
                    <h1>{t('changelog.heading')}</h1>
                </PageHeader>
                <Timeline>
                    {releases.map((release, index) => (
                        <Release key={release.tag} $latest={index === 0}>
                            <ReleaseHeading>
                                <Version>{release.version}</Version>
                                <ReleaseDate>{formatDate(release.date, dateLocale)}</ReleaseDate>
                            </ReleaseHeading>
                            <Notes
                                $latest={index === 0}
                                dangerouslySetInnerHTML={{ __html: release.html }}
                            />
                        </Release>
                    ))}
                </Timeline>
                <DownloadRow>
                    <StoreLinks linkVersion={false} />
                </DownloadRow>
                <Spacer size={90} />
            </div>
            <Footer />
        </div>
    )
}
